import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import RequestDetail from './RequestDetail';
import { Loader2, ArrowLeft, Inbox } from 'lucide-react';
import { formatLongDate } from '../utils/datetime';

export default function AdminRequests({ user }) {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
        if (selectedRequest) {
          const updated = data.find(r => r.id === selectedRequest.id);
          if (updated) setSelectedRequest(updated);
          else setSelectedRequest(null);
        }
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
    const matchesSearch = req.formName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         req.initiatorName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusCount = (status) => {
    if (status === 'all') return requests.length;
    return requests.filter(r => r.status === status).length;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="submissions-view">
      <div className="submissions-header">
        <div>
          <h1 className="submissions-title">All Requests</h1>
          <p className="dashboard-subtitle">Monitor all approval requests across the system</p>
        </div>
      </div>

      <div className="dashboard-filters">
        <input
          type="text"
          placeholder="Search by form or initiator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filter-buttons">
          <button
            onClick={() => setFilterStatus('all')}
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
          >
            All ({getStatusCount('all')})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
          >
            Pending ({getStatusCount('pending')})
          </button>
          <button
            onClick={() => setFilterStatus('approved')}
            className={`filter-btn ${filterStatus === 'approved' ? 'active' : ''}`}
          >
            Approved ({getStatusCount('approved')})
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`filter-btn ${filterStatus === 'rejected' ? 'active' : ''}`}
          >
            Rejected ({getStatusCount('rejected')})
          </button>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="empty-state">
          <Inbox size={48} />
          <h3>No requests found</h3>
          <p>No approval requests match your filters</p>
        </div>
      ) : (
        <div className="submissions-content">
          <div className="submissions-list">
            {filteredRequests.map(request => (
              <div
                key={request.id}
                className={`submission-item ${selectedRequest?.id === request.id ? 'active' : ''}`}
                onClick={() => setSelectedRequest(request)}
              >
                <div className="submission-item-header">
                  <span className={`status-badge status-${request.status}`}>
                    {request.status}
                  </span>
                  <span className="submission-date">
                    {formatLongDate(request.createdAt)}
                  </span>
                </div>
                <div className="submission-item-preview">
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Form:</span>
                    <span className="preview-value-sm">{request.formName}</span>
                  </div>
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Initiator:</span>
                    <span className="preview-value-sm">{request.initiatorName}</span>
                  </div>
                  <div className="preview-field-sm">
                    <span className="preview-label-sm">Current:</span>
                    <span className="preview-value-sm">{request.currentNodeLabel || 'Completed'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="submission-detail">
            {selectedRequest ? (
              <RequestDetail
                request={selectedRequest}
                user={user}
                onUpdate={loadRequests}
                isAdmin={true}
              />
            ) : (
              <div className="detail-empty">
                <div style={{ textAlign: 'center' }}>
                  <ArrowLeft size={48} style={{ marginBottom: '16px' }} />
                  <p style={{ color: '#64748b' }}>Select a request to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
